import { useState, useRef, useEffect } from "react";
import Consultation from "../pages/Consultation"; // ← adjust path to match your project
import SEO from "../components/SEO";

const steps = [
  {
    num: "01",
    title: "Clinical Design & Space Planning",
    desc: "Our proprietary Canvas platform creates 2D→3D layouts for OTs, ICUs, wards & corridors in half the time of traditional tools. Every millimetre is accounted for before a single panel is cut.",
    tags: ["2D→3D Conversion", "NABH Compliance", "Workflow Optimisation"],
  },
  {
    num: "02",
    title: "Medical-Grade Material Sourcing",
    desc: "Antibacterial surfaces, HEPA-compatible panels, and infection-control finishes tested at NABL-certified labs. Nothing is taken for granted — only the best materials enter your hospital.",
    tags: ["NABL Testing", "Moisture Resistance", "VOC Emission Tests"],
  },
  {
    num: "03",
    title: "Precision Manufacturing",
    desc: "CNC machines read design files directly — zero manual entry, zero dimensional errors. QR codes track every panel through the automated production line seamlessly.",
    tags: ["CNC Cutting", "AntiBubble® Bonding", "AquaBloc® Edge Sealing"],
  },
  {
    num: "04",
    title: "Perfection Through Inspection",
    desc: "Every panel is checked for scratches, chip-offs, air pockets, sharp edges, dimensional errors and colour shades before it leaves our facility. Zero compromises.",
    tags: ["100-Point Check", "Colour Calibration", "Dimensional Accuracy"],
  },
  {
    num: "05",
    title: "Sterile Packaging & Transit",
    desc: "Medical-grade flat-pack with sealed wrapping, corner protectors and drop-tested packaging ensures zero damage in transit — your units arrive pristine every time.",
    tags: ["Drop Tested", "Sterile Wrapping", "Zero Transit Damage"],
  },
  {
    num: "06",
    title: "Swift Certified Installation",
    desc: "Trained and certified teams assemble modular units on-site with minimal dust and downtime — even in live hospital wings. Handover in 45 days on average.",
    tags: ["Certified Teams", "Live-Wing Safe", "45-Day Handover"],
  },
];

const departments = [
  {
    icon: "⊕",
    title: "Operating Theatres",
    sub: "Modular OT suites",
    desc: "Positive-pressure laminar-airflow OT suites with integrated utility chases, medical gas panels and seamless PVC wall cladding.",
    specs: ["Positive pressure airflow", "Anti-static flooring", "Medical gas integration", "Seamless PVC cladding"],
  },
  {
    icon: "◈",
    title: "ICU & Critical Care",
    sub: "Glass-partition bays",
    desc: "ICU bays with central nursing visibility, headwall service panels, infection-control finishes and cable management built into every partition.",
    specs: ["Tempered glass bays", "Headwall panels", "360° nurse visibility", "Easy-clean coatings"],
  },
  {
    icon: "◎",
    title: "Diagnostic Centres",
    sub: "EMI-shielded rooms",
    desc: "Radiology, pathology and imaging rooms with EMI shielding, lead-lined panels, and workflow-optimised layouts for equipment-specific clearances.",
    specs: ["Lead-lined walls", "EMI shielding", "Radiation compliance", "Dark-room fitout"],
  },
  {
    icon: "▣",
    title: "Patient Wards",
    sub: "Antimicrobial built-ins",
    desc: "Modular ward furniture with DuraBuild™ cabinets, AquaBloc® moisture-sealed edges, antimicrobial laminates and soft-close clinical hardware.",
    specs: ["AquaBloc® moisture seal", "350kg load rating", "Antimicrobial finish", "Soft-close hardware"],
  },
  {
    icon: "⬡",
    title: "Pharmacy & Labs",
    sub: "Controlled storage",
    desc: "Cold-chain compliant storage fitouts, controlled-access dispensary counters and HEPA-rated clean-room partitions for sensitive environments.",
    specs: ["Cold-chain fitout", "Controlled access", "HEPA partitions", "Sterile zones"],
  },
  {
    icon: "◉",
    title: "Emergency & Triage",
    sub: "Rapid-response zones",
    desc: "Fast-track emergency layouts with crash-cart bays, trauma room partitions and colour-coded wayfinding integrated into the architecture.",
    specs: ["Crash-cart bays", "Trauma rooms", "Wayfinding system", "Rapid assembly"],
  },
];

const warrantyTests = [
  ["Drawer cyclic test", "100,000 cycles"],
  ["Shutter cyclic test", "EN 15186 compliant"],
  ["Vertical force test", "Hinge integrity verified"],
  ["Shelf load test", "350kg rated"],
  ["Drop transit test", "Zero damage guaranteed"],
  ["Moisture resistance", "AquaBloc® sealed edges"],
];

const techTags = [
  "NABL Certified Testing","NABH Compliant Layouts","DuraBuild™ Cabinets",
  "AntiBubble® Technology","AquaBloc® Edge Sealing","CNC Precision Cutting",
  "QR Production Tracking","TUV Rheinland Tested","VOC Emission Tested",
  "IS 1893 Seismic Compliant","4-Sigma Process","European EN 527 Standard",
];

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0px)" : "translateY(28px)",
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

const S = {
  // colours
  black: "#343F86",
  white: "#ffffff",
  offWhite: "#F6F7FC",
  ash1: "#F6F7FC",   // lightest ash
  ash2: "#d6d5d0",   // light ash border
  ash3: "#A7ACBD",   // mid ash
  ash4: "#5B6072",   // dark ash text
};

export default function HospitalInfrastructure() {
  const [activeStep, setActiveStep] = useState(0);
  const [showConsultation, setShowConsultation] = useState(false);

  const openConsultation = () => setShowConsultation(true);
  const closeConsultation = () => setShowConsultation(false);

  const css = `
    *{box-sizing:border-box;margin:0;padding:0}
    .hr{font-family: 'Inter', 'Montserrat', sans-serif;background:${S.offWhite};color:${S.black};line-height:1.6}
    .hr-hero{display:grid;grid-template-columns:1fr 1fr;min-height:88vh}
    .hr-hero-l{background:${S.black};padding:9rem 3.5rem 4rem;display:flex;flex-direction:column;justify-content:space-between}
    .hr-hero-r{background:${S.ash1};padding:4rem 3rem;display:flex;flex-direction:column;justify-content:center;border-left:1px solid ${S.ash2}}
    .hr-eyebrow{font-size:11px;letter-spacing:3px;text-transform:uppercase;color:${S.ash3};margin-bottom:2rem}
    .hr-h1{font-family: 'Inter', 'Montserrat', sans-serif;font-size:56px;font-weight:700;line-height:1.1;letter-spacing:-1.5px;color:${S.white};margin-bottom:1.5rem}
    .hr-h1 em{font-style:italic;color:${S.ash3}}
    .hr-hero-p{font-size:16px;color:${S.ash3};line-height:1.8;max-width:380px;margin-bottom:2.5rem}
    .hr-btn-w{background:${S.white};color:${S.black};border:none;padding:13px 28px;font-family: 'Inter', 'Montserrat', sans-serif;font-size:13px;font-weight:500;border-radius:4px;cursor:pointer;transition:background .2s,transform .15s}
    .hr-btn-w:hover{background:#E1E3EE;transform:translateY(-1px)}
    .hr-btn-o{background:transparent;color:${S.ash3};border:1px solid #262E63;padding:13px 28px;font-family: 'Inter', 'Montserrat', sans-serif;font-size:13px;border-radius:4px;cursor:pointer;transition:border-color .2s,color .2s}
    .hr-btn-o:hover{border-color:#8A8F9C;color:${S.white}}
    .hr-stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:0;border-top:1px solid #262E63;margin-top:3rem}
    .hr-stat{padding:1.5rem 0;border-right:1px solid #262E63}
    .hr-stat:last-child{border-right:none}
    .hr-stat-v{font-family: 'Inter', 'Montserrat', sans-serif;font-size:28px;font-weight:700;color:${S.white}}
    .hr-stat-l{font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#2F8A98;margin-top:4px}
    .hr-dept-list{display:flex;flex-direction:column}
    .hr-dept-row{display:flex;align-items:center;justify-content:space-between;padding:1rem 0;border-bottom:1px solid ${S.ash2};cursor:default;transition:padding-left .2s}
    .hr-dept-row:first-child{border-top:1px solid ${S.ash2}}
    .hr-dept-row:hover{padding-left:6px}
    .hr-dept-row-l{display:flex;align-items:center;gap:14px}
    .hr-dept-n{font-size:11px;color:${S.ash3};width:22px}
    .hr-dept-name{font-size:15px;font-weight:500;color:${S.black}}
    .hr-dept-arr{font-size:12px;color:${S.ash3}}
    .hr-cert{margin-top:2rem;background:${S.white};border:1px solid ${S.ash2};border-radius:6px;padding:1.25rem}
    .hr-cert-l{font-size:11px;text-transform:uppercase;letter-spacing:2px;color:${S.ash4};margin-bottom:10px}
    .hr-cert-row{display:flex;gap:8px;flex-wrap:wrap}
    .hr-cert-b{background:${S.ash1};padding:4px 12px;border-radius:3px;font-size:12px;color:${S.ash4};font-weight:500}

    .hr-page{padding:0 3.5rem}

    .hr-sec{padding:5rem 0;border-top:1px solid ${S.ash2}}
    .hr-sec-ey{font-size:11px;letter-spacing:3px;text-transform:uppercase;color:${S.ash3};margin-bottom:0.75rem}
    .hr-sec-h2{font-family: 'Inter', 'Montserrat', sans-serif;font-size:40px;font-weight:700;letter-spacing:-1px;color:${S.black};margin-bottom:0.75rem;line-height:1.15}
    .hr-sec-h2 em{font-style:italic;color:${S.ash4}}
    .hr-sec-d{font-size:16px;color:${S.ash4};line-height:1.8;max-width:520px}

    .hr-journey{display:grid;grid-template-columns:280px 1fr;gap:2rem;margin-top:3rem;align-items:start}
    .hr-j-nav{border:1px solid ${S.ash2};border-radius:8px;overflow:hidden;position:sticky;top:1.5rem}
    .hr-j-item{display:flex;align-items:center;gap:12px;padding:1rem 1.25rem;border-bottom:1px solid ${S.ash2};cursor:pointer;background:${S.white};transition:background .15s}
    .hr-j-item:last-child{border-bottom:none}
    .hr-j-item.act{background:${S.black}}
    .hr-j-item:not(.act):hover{background:${S.ash1}}
    .hr-j-num{width:26px;height:26px;border-radius:50%;background:${S.ash1};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;color:${S.ash4};flex-shrink:0;transition:background .15s,color .15s}
    .hr-j-item.act .hr-j-num{background:${S.white};color:${S.black}}
    .hr-j-title{font-size:13px;font-weight:500;color:${S.black};line-height:1.4;transition:color .15s}
    .hr-j-item.act .hr-j-title{color:${S.white}}
    .hr-j-detail{background:${S.white};border:1px solid ${S.ash2};border-radius:8px;padding:2.5rem}
    .hr-j-big{font-family: 'Inter', 'Montserrat', sans-serif;font-size:56px;font-weight:700;color:${S.ash1};line-height:1;letter-spacing:-2px;margin-bottom:0.75rem}
    .hr-j-dtitle{font-family: 'Inter', 'Montserrat', sans-serif;font-size:20px;font-weight:600;color:${S.black};margin-bottom:1rem;letter-spacing:-0.3px}
    .hr-j-ddesc{font-size:16px;color:${S.ash4};line-height:1.8;margin-bottom:2rem}
    .hr-j-tags{display:flex;gap:8px;flex-wrap:wrap}
    .hr-j-tag{background:${S.black};color:${S.white};padding:6px 16px;border-radius:20px;font-size:12px;font-weight:500}

    .hr-depts-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:0;border:1px solid ${S.ash2};border-radius:8px;overflow:hidden;margin-top:3rem}
    .hr-dc{background:${S.white};padding:2rem;border-right:1px solid ${S.ash2};border-bottom:1px solid ${S.ash2};cursor:default;transition:background .2s}
    .hr-dc:nth-child(3n){border-right:none}
    .hr-dc:nth-child(n+4){border-bottom:none}
    .hr-dc:hover{background:${S.black}}
    .hr-dc:hover .dc-ico,.hr-dc:hover .dc-title,.hr-dc:hover .dc-sub,.hr-dc:hover .dc-desc,.hr-dc:hover .dc-spec{color:inherit}
    .hr-dc:hover .dc-title{color:${S.white}}
    .hr-dc:hover .dc-sub{color:${S.ash3}}
    .hr-dc:hover .dc-desc{color:#8A8F9C}
    .hr-dc:hover .dc-spec{color:#5B6072;border-bottom-color:#262E63}
    .hr-dc:hover .dc-ico{color:${S.ash3}}
    .dc-ico{font-size:22px;color:${S.ash3};margin-bottom:1rem;display:block;transition:color .2s}
    .dc-title{font-family: 'Inter', 'Montserrat', sans-serif;font-size:20px;font-weight:600;color:${S.black};margin-bottom:4px;transition:color .2s}
    .dc-sub{font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:${S.ash3};margin-bottom:1rem;transition:color .2s}
    .dc-desc{font-size:13px;color:${S.ash4};line-height:1.7;margin-bottom:1rem;transition:color .2s}
    .dc-spec{font-size:13px;color:${S.ash4};padding:5px 0;border-bottom:1px solid ${S.ash2};display:flex;align-items:center;gap:6px;transition:color .2s,border-bottom-color .2s}
    .dc-spec::before{content:'—';font-size:9px;color:${S.ash3}}

    .hr-tech{background:${S.black};padding:4rem 3.5rem;margin-top:0}
    .hr-tech-inner{display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:start}
    .hr-tech-h2{font-family: 'Inter', 'Montserrat', sans-serif;font-size:40px;font-weight:700;letter-spacing:-1px;color:${S.white};margin-bottom:0.75rem;line-height:1.15}
    .hr-tech-d{font-size:16px;color:#8A8F9C;line-height:1.8}
    .hr-tech-tags{display:flex;flex-wrap:wrap;gap:8px}
    .hr-t-tag{background:#343F86;border:1px solid #262E63;color:#8A8F9C;padding:7px 14px;border-radius:20px;font-size:12px;transition:border-color .15s,color .15s;cursor:default}
    .hr-t-tag:hover{border-color:#5B6072;color:${S.white}}

    .hr-warranty{display:grid;grid-template-columns:1fr 1fr;gap:2rem;margin-top:3rem}
    .hr-w-card{background:${S.white};border:1px solid ${S.ash2};border-radius:8px;padding:2rem}
    .hr-w-big{font-family: 'Inter', 'Montserrat', sans-serif;font-size:48px;font-weight:700;color:${S.black};letter-spacing:-1.5px;line-height:1;margin-bottom:6px}
    .hr-w-lbl{font-size:11px;text-transform:uppercase;letter-spacing:2px;color:${S.ash3};margin-bottom:1.25rem}
    .hr-w-desc{font-size:16px;color:${S.ash4};line-height:1.8}
    .hr-w-tests{margin-top:1.5rem}
    .hr-w-test{display:flex;justify-content:space-between;align-items:center;padding:0.85rem 0;border-bottom:1px solid ${S.ash1}}
    .hr-w-test:last-child{border-bottom:none}
    .hr-w-tn{font-size:14px;color:${S.black};font-weight:500}
    .hr-w-tv{font-size:12px;color:${S.ash4}}

    .hr-cta{background:${S.black};border-radius:8px;padding:4rem;margin:5rem 0;display:grid;grid-template-columns:1fr auto;gap:3rem;align-items:center}
    .hr-cta-h2{font-family: 'Inter', 'Montserrat', sans-serif;font-size:40px;font-weight:700;letter-spacing:-1px;color:${S.white};margin-bottom:0.75rem;line-height:1.15}
    .hr-cta-p{font-size:16px;color:#8A8F9C;line-height:1.8;max-width:400px}
    .hr-cta-btns{display:flex;flex-direction:column;gap:10px;min-width:180px}

    @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    .fa{animation:fadeUp .7s ease forwards}
    .d1{animation-delay:.1s}.d2{animation-delay:.2s}.d3{animation-delay:.35s}

    /* ── CONSULTATION MODAL ── */
    .hr-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px}
    .hr-modal-box{position:relative;background:${S.black};border-radius:12px;max-width:560px;width:100%;max-height:90vh;overflow-y:auto}
    .hr-modal-close{position:absolute;top:12px;right:16px;background:transparent;border:none;color:${S.white};font-size:28px;cursor:pointer;line-height:1;z-index:10}

    /* ── MOBILE ── */
    @media (max-width: 768px) {
      .hr-hero{grid-template-columns:1fr;min-height:auto}
      .hr-hero-l{padding:3rem 1.25rem 2.5rem}
      .hr-hero-r{padding:2.5rem 1.25rem;border-left:none;border-top:1px solid ${S.ash2}}
      .hr-h1{font-size:32px;letter-spacing:-0.5px}
      .hr-hero-p{font-size:15px;max-width:100%}
      .hr-stats-row{grid-template-columns:repeat(2,1fr)}
      .hr-stat:nth-child(2){border-right:none}
      .hr-stat-v{font-size:24px}

      .hr-page{padding:0 1.25rem}
      .hr-sec{padding:2.75rem 0}
      .hr-sec-h2{font-size:26px}
      .hr-sec-d{font-size:15px}

      .hr-journey{grid-template-columns:1fr;gap:1.25rem}
      .hr-j-nav{position:static}
      .hr-j-detail{padding:1.75rem}
      .hr-j-big{font-size:40px}
      .hr-j-dtitle{font-size:18px}
      .hr-j-ddesc{font-size:15px}

      .hr-depts-grid{grid-template-columns:1fr}
      .hr-dc{border-right:none !important;border-bottom:1px solid ${S.ash2} !important}
      .dc-title{font-size:18px}
      .dc-desc{font-size:13px}
      .dc-spec{font-size:12px}

      .hr-tech{padding:2.75rem 1.25rem}
      .hr-tech-inner{grid-template-columns:1fr;gap:1.75rem}
      .hr-tech-h2{font-size:26px}
      .hr-tech-d{font-size:15px}

      .hr-warranty{grid-template-columns:1fr;gap:1.25rem}
      .hr-w-big{font-size:36px}
      .hr-w-desc{font-size:15px}

      .hr-cta{grid-template-columns:1fr;padding:2.5rem 1.5rem;gap:1.75rem;text-align:left}
      .hr-cta-h2{font-size:26px}
      .hr-cta-p{font-size:15px;max-width:100%}
    }
  `;

  return (
     <>
    <SEO/>
    <div className="hr">
      <style>{css}</style>

      {/* HERO */}
      <section className="hr-hero">
        <div className="hr-hero-l">
          <div>
            <p className="hr-eyebrow fa">Healthcare Infrastructure</p>
            <h1 className="hr-h1 fa d1">Spaces that<br /><em>heal.</em></h1>
            <p className="hr-hero-p fa d2">
              India's only end-to-end automated hospital design-to-installation process. Engineered for zero errors, clinical-grade precision, and lifelong durability.
            </p>
            <div style={{ display:"flex", gap:12 }} className="fa d3">
              <button className="hr-btn-w" onClick={openConsultation}>Get started</button>
              <button className="hr-btn-o">View projects</button>
            </div>
          </div>
          <div className="hr-stats-row fa">
            {[["500+","Hospitals built"],["99.9%","Accuracy"],["45 days","Avg handover"],["Lifetime","Warranty"]].map(([v,l],i)=>(
              <div className="hr-stat" key={i}>
                <div className="hr-stat-v">{v}</div>
                <div className="hr-stat-l">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hr-hero-r">
          <p className="hr-eyebrow">Departments we build</p>
          <div className="hr-dept-list">
            {departments.map((d,i)=>(
              <div className="hr-dept-row" key={i}>
                <div className="hr-dept-row-l">
                  <span className="hr-dept-n">0{i+1}</span>
                  <span className="hr-dept-name">{d.title}</span>
                </div>
                <span className="hr-dept-arr">→</span>
              </div>
            ))}
          </div>
          <div className="hr-cert">
            <div className="hr-cert-l">Certifications</div>
            <div className="hr-cert-row">
              {["NABH","NABL","ISO 9001","TUV Rheinland","JCI"].map(c=>(
                <span className="hr-cert-b" key={c}>{c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="hr-page">
        {/* JOURNEY */}
        <Reveal>
          <section className="hr-sec">
            <p className="hr-sec-ey">The modular journey</p>
            <h2 className="hr-sec-h2">Design to <em>handover</em></h2>
            <p className="hr-sec-d">Every hospital follows the same rigorous six-stage automated process — zero manual measurement, zero room for error.</p>
            <div className="hr-journey">
              <nav className="hr-j-nav">
                {steps.map((s,i)=>(
                  <div key={i} className={`hr-j-item${activeStep===i?" act":""}`} onClick={()=>setActiveStep(i)}>
                    <div className="hr-j-num">{i+1}</div>
                    <div className="hr-j-title">{s.title}</div>
                  </div>
                ))}
              </nav>
              <div className="hr-j-detail">
                <div className="hr-j-big">{steps[activeStep].num}</div>
                <h3 className="hr-j-dtitle">{steps[activeStep].title}</h3>
                <p className="hr-j-ddesc">{steps[activeStep].desc}</p>
                <div className="hr-j-tags">
                  {steps[activeStep].tags.map((t,i)=><span key={i} className="hr-j-tag">{t}</span>)}
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* DEPARTMENTS */}
        <Reveal delay={0.05}>
          <section className="hr-sec">
            <p className="hr-sec-ey">Core departments</p>
            <h2 className="hr-sec-h2">Spaces we <em>design</em></h2>
            <p className="hr-sec-d">From operating theatres to emergency zones — every department demands its own clinical logic.</p>
            <div className="hr-depts-grid">
              {departments.map((d,i)=>(
                <div key={i} className="hr-dc">
                  <span className="dc-ico">{d.icon}</span>
                  <div className="dc-title">{d.title}</div>
                  <div className="dc-sub">{d.sub}</div>
                  <p className="dc-desc">{d.desc}</p>
                  <div>{d.specs.map((s,j)=><div key={j} className="dc-spec">{s}</div>)}</div>
                </div>
              ))}
            </div>
          </section>
        </Reveal>
      </div>

      {/* TECH STRIP full bleed */}
      <Reveal delay={0.05}>
        <div className="hr-tech">
          <div className="hr-tech-inner">
            <div>
              <h2 className="hr-tech-h2">Technology &<br />compliance</h2>
              <p className="hr-tech-d">Every technology we deploy is verified by independent third-party laboratories. No self-certification, ever.</p>
            </div>
            <div className="hr-tech-tags">
              {techTags.map((t,i)=><span key={i} className="hr-t-tag">{t}</span>)}
            </div>
          </div>
        </div>
      </Reveal>

      <div className="hr-page">
        {/* WARRANTY */}
        <Reveal delay={0.05}>
          <section className="hr-sec">
            <p className="hr-sec-ey">Built to last</p>
            <h2 className="hr-sec-h2">Warranty built on <em>confidence</em></h2>
            <p className="hr-sec-d">Every modular unit is backed by a lifetime structural warranty — independently tested at TUV Rheinland-certified facilities.</p>
            <div className="hr-warranty">
              <div className="hr-w-card">
                <div className="hr-w-big">∞</div>
                <div className="hr-w-lbl">Lifetime warranty</div>
                <p className="hr-w-desc">Our DuraBuild™ cabinet systems are rated for 350kg load capacity and tested to European standards. What you install today will outlast the building itself.</p>
              </div>
              <div className="hr-w-card">
                <p style={{fontSize:11,textTransform:"uppercase",letterSpacing:2,color:S.ash3,marginBottom:"1.25rem"}}>Performance tests</p>
                <div className="hr-w-tests">
                  {warrantyTests.map(([n,v],i)=>(
                    <div key={i} className="hr-w-test">
                      <span className="hr-w-tn">{n}</span>
                      <span className="hr-w-tv">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* CTA */}
        <Reveal delay={0.05}>
          <div className="hr-cta">
            <div>
              <h2 className="hr-cta-h2">Your hospital deserves<br />precision engineering.</h2>
              <p className="hr-cta-p">Talk to our healthcare infrastructure specialists. Free site assessment and design consultation — no obligation.</p>
            </div>
            <div className="hr-cta-btns">
              <button className="hr-btn-w" onClick={openConsultation}>Book a consultation</button>
              <button className="hr-btn-o">Download brochure</button>
            </div>
          </div>
        </Reveal>
      </div>

      {/* ── CONSULTATION MODAL ── */}
      {showConsultation && (
        <div className="hr-modal-overlay" onClick={closeConsultation}>
          <div className="hr-modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="hr-modal-close" onClick={closeConsultation}>×</button>
            <Consultation />
          </div>
        </div>
      )}
    </div>
    </>
  );
}